import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import {
  Complement,
  euros,
  listComplements,
  messageErreur,
  reportComplementTransfer,
} from './complementApi';

/**
 * Vue agence d'un complément (spécification « CG côté agence » v1.3, §10.10) :
 * le complément est facturé dès sa création par Skipcar, sans accord de
 * l'agence (16/10/2026) ; l'agence le consulte et règle sa facture (bloc virement).
 */

const VIOLET = '#6d28d9';

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const BlocComplement = ({
  complement,
  busy,
  onTransfer,
  onQuestion,
}: {
  complement: Complement;
  busy: boolean;
  onTransfer: (c: Complement) => void;
  onQuestion: () => void;
}) => {
  const aRegler = complement.statut === 'accepte' && complement.modeFacturation === 'dossier';
  const surReleve = complement.statut === 'accepte' && complement.modeFacturation === 'releve';

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
      <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
        {aRegler
          ? 'Une facture complémentaire est en attente.'
          : surReleve
            ? 'Complément ajouté à votre prochain relevé.'
            : 'Complément réglé.'}
      </Typography>

      <Typography sx={{ fontWeight: 600, mb: 1 }}>{complement.titre}.</Typography>
      {complement.texte && (
        <Typography sx={{ whiteSpace: 'pre-line', mb: 2 }}>{complement.texte}</Typography>
      )}
      {aRegler && (
        <Typography sx={{ mb: 2 }}>
          Nous avons lancé la démarche. La carte grise n'est validée qu'une fois ce complément réglé ; l'ANTS met en
          moyenne 30 jours.
        </Typography>
      )}
      {surReleve && (
        <Typography sx={{ mb: 2 }}>
          Nous avons lancé la démarche. La carte grise est validée une fois la démarche complémentaire aboutie ; les
          délais du CERT sont en moyenne de 30 jours. Le complément sera facturé sur votre prochaine facture.
        </Typography>
      )}

      <Box sx={{ border: `1px solid ${VIOLET}33`, background: `${VIOLET}0d`, borderRadius: 2, p: 2, mb: 2 }}>
        {complement.items.map((item, index) => (
          <Stack key={index} direction="row" justifyContent="space-between" spacing={2}>
            <Typography>
              {item.title}
              {Number(item.quantity) !== 1 ? ` × ${item.quantity}` : ''}
            </Typography>
            <Typography>
              {(Number(item.quantity) * Number(item.unit_price.value)).toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'EUR',
              })}{' '}
              HT
            </Typography>
          </Stack>
        ))}
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, color: VIOLET, fontWeight: 700 }}>
          <Typography fontWeight={700}>Montant</Typography>
          <Typography fontWeight={700}>{euros(complement.montantTtcCentimes)} TTC</Typography>
        </Stack>
      </Box>

      {aRegler && complement.facture && (
        <Box sx={{ background: '#f8f8f8', borderRadius: 2, p: 2, mb: 2 }}>
          <Typography variant="body2">
            Virement de <strong>{euros(complement.montantTtcCentimes)}</strong> sur le compte{' '}
            <strong>{complement.iban}</strong>, référence <strong>{complement.facture.numero}</strong>.
          </Typography>
          {complement.virementSignaleLe && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Signalé le {formatDate(complement.virementSignaleLe)}. Nous le rapprochons dès réception ; la démarche
              continue en attendant.
            </Typography>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button variant="outlined" disabled={busy} onClick={onQuestion}>
          Poser une question
        </Button>
        {aRegler && !complement.virementSignaleLe && (
          <Button variant="contained" disabled={busy} onClick={() => onTransfer(complement)}>
            J'ai fait le virement
          </Button>
        )}
        {complement.facture?.url && (
          <Button variant="outlined" href={complement.facture.url} target="_blank" rel="noreferrer">
            Facture {complement.facture.numero}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export interface AgencyComplementDialogProps {
  open: boolean;
  onClose: () => void;
  registrationId: number;
  regPlate: string;
  dealId?: number | null;
  onChanged?: () => void | Promise<void>;
}

const AgencyComplementDialog = ({
  open,
  onClose,
  registrationId,
  regPlate,
  dealId,
  onChanged,
}: AgencyComplementDialogProps) => {
  const [complements, setComplements] = useState<Complement[]>([]);
  // La liste n'est rafraîchie qu'à la fermeture : rafraîchie tout de suite, elle
  // déplace le dossier dans une autre section et démonte cette fenêtre avant
  // que l'utilisateur voie le résultat (constaté en recette, 10/10/2026).
  const [aModifie, setAModifie] = useState(false);
  const fermer = () => {
    onClose();
    if (aModifie) {
      setAModifie(false);
      void onChanged?.();
    }
  };
  const [chargement, setChargement] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const recharger = async () => {
    setChargement(true);
    try {
      setComplements(await listComplements(registrationId));
    } catch (error) {
      setErreur(messageErreur(error, 'Impossible de charger le complément.'));
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setErreur(null);
    setInfo(null);
    void recharger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, registrationId]);

  const signalerVirement = async (c: Complement) => {
    setBusy(true);
    setErreur(null);
    try {
      await reportComplementTransfer(c.id);
      setInfo('Merci, nous rapprochons votre virement dès réception.');
      await recharger();
      setAModifie(true);
    } catch (error) {
      setErreur(messageErreur(error, "Le virement n'a pas pu être signalé."));
    } finally {
      setBusy(false);
    }
  };

  const poserQuestion = () => {
    fermer();
    if (dealId) window.location.assign(`/list-cg?openExternalComments=${dealId}`);
  };

  // Le serveur ne renvoie à l'agence que les compléments facturés ; filtre gardé par sûreté.
  const visibles = complements.filter((c) => c.statut !== 'annule' && c.statut !== 'propose');

  return (
    <Dialog open={open} onClose={busy ? undefined : fermer} fullWidth maxWidth="sm">
      <DialogTitle>Complément · {regPlate}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {erreur && <Alert severity="error">{erreur}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}
          {chargement ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : visibles.length === 0 ? (
            <Typography color="text.secondary">Aucun complément sur ce dossier.</Typography>
          ) : (
            visibles.map((c) => (
              <BlocComplement
                key={c.id}
                complement={c}
                busy={busy}
                onTransfer={(x) => void signalerVirement(x)}
                onQuestion={poserQuestion}
              />
            ))
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={fermer} disabled={busy}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgencyComplementDialog;
